import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { CheckCircle2 } from "lucide-react";
import IncontiLogo from "@/components/IncontiLogo";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError("Token faltante"); setLoadingInfo(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/activation/${token}`);
        setInfo(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Token inválido o expirado");
      } finally {
        setLoadingInfo(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Mínimo 6 caracteres"); return; }
    if (password !== confirm) { setError("Las contraseñas no coinciden"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Error al activar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 grid-bg">
      <div className="w-full max-w-md panel-strong scan-line">
        <div className="px-8 pt-8 pb-6 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="mb-6">
            <IncontiLogo size={40} />
          </div>
          <div className="label-mono">// Activación de cuenta</div>
          <h1 className="font-display font-black text-2xl tracking-wide mt-1 text-slate-100">
            {info ? "Define tu contraseña" : "Activar cuenta"}
          </h1>
          {info && (
            <p className="text-sm text-slate-400 mt-2">
              Hola <span className="text-slate-100 font-semibold">{info.name}</span>, completa tu contraseña para acceder.
            </p>
          )}
        </div>

        <div className="px-8 py-6">
          {loadingInfo && <div className="text-slate-400 text-sm text-center label-mono">Validando token…</div>}

          {!loadingInfo && !info && (
            <div className="text-center py-6">
              <div className="text-sm rounded-sm px-3 py-2 badge-danger inline-block">
                {error || "Token inválido o expirado"}
              </div>
              <p className="text-xs text-slate-500 mt-4">Solicita al administrador un nuevo enlace.</p>
            </div>
          )}

          {info && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label-mono block mb-1.5">Email</label>
                <input value={info.email} disabled className="w-full input-cyber px-3 py-2.5 text-sm opacity-70" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">Nueva contraseña</label>
                <input type="password" required data-testid="set-pw-password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full input-cyber px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">Confirmar contraseña</label>
                <input type="password" required data-testid="set-pw-confirm" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full input-cyber px-3 py-2.5 text-sm" />
              </div>
              {error && (
                <div data-testid="set-pw-error" className="text-sm px-3 py-2 rounded-sm badge-danger">{error}</div>
              )}
              <button type="submit" data-testid="set-pw-submit-btn" disabled={submitting}
                className="w-full btn-primary py-2.5 rounded-sm tracking-widest text-sm uppercase disabled:opacity-50 flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {submitting ? "Activando…" : "Activar cuenta"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
